import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const GRAVITY = 0.35;
const JUMP_FORCE = -6.5;
const PIPE_WIDTH = 50;
const PIPE_GAP_MIN = 110;
const PIPE_GAP_MAX = 130;
const PIPE_SPEED = 2.75;  // 10% faster than original 2.5
const BAT_WIDTH = 50;
const BAT_HEIGHT = 35;
const BAT_HITBOX_PADDING = 10;
const GAME_HEIGHT = 400;
const GAME_WIDTH = 350;
const BAT_X = 60;

export default function FlappyBat({ user }) {
  const [gameState, setGameState] = useState("idle"); // "idle", "playing", "gameover"
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [serverHighScore, setServerHighScore] = useState(0);
  const [batY, setBatY] = useState(GAME_HEIGHT / 2 - BAT_HEIGHT / 2);
  const [batVelocity, setBatVelocity] = useState(0);
  const [pipes, setPipes] = useState([]);
  const [wingUp, setWingUp] = useState(false);
  const [newRecord, setNewRecord] = useState(false);
  
  // Validation tracking (obfuscated variable names)
  const [_vt, set_vt] = useState(null);  // Session token
  const [_vg, set_vg] = useState(0);  // Game start time
  const _xi = useRef(0);  // Interaction counter (jumps)
  const _op = useRef(0);  // Obstacles passed
  
  const gameContainerRef = useRef(null);

  // Fetch user's high score from server on mount
  useEffect(() => {
    if (user) {
      fetchServerHighScore();
    }
  }, [user]);

  const fetchServerHighScore = async () => {
    try {
      const response = await fetch(`${API}/rankings/flappy-bat`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        if (data.user_rank) {
          setServerHighScore(data.user_rank.score);
          setHighScore(data.user_rank.score);
        }
      }
    } catch (error) {
      console.error("Error fetching high score:", error);
    }
  };

  const saveHighScore = async (newScore) => {
    if (!user || newScore <= 0) return;
    
    const gameDuration = Date.now() - _vg;
    
    try {
      const response = await fetch(`${API}/user/cave-bat/score`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          score: newScore,
          tk: _vt,
          op: _op.current,
          xi: _xi.current,
          gd: gameDuration
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.is_new_best && data.validated) {
          setServerHighScore(newScore);
          setNewRecord(true);
          toast.success(`New high score: ${newScore}!`);
        }
      }
    } catch (error) {
      console.error("Error saving high score:", error);
    }
  };

  // Start game session with server (for validation)
  const initGameSession = async () => {
    if (!user) return null;
    
    const clientTime = Date.now();
    try {
      const response = await fetch(`${API}/user/cave-bat/start-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ct: clientTime })
      });
      
      if (response.ok) {
        const data = await response.json();
        return { token: data.tk, startTime: clientTime };
      }
    } catch (error) {
      console.error("Session init error");
    }
    return null;
  };

  // Generate a new pipe with variable gap
  const createPipe = useCallback(() => {
    // Randomize gap size between min and max
    const pipeGap = Math.floor(Math.random() * (PIPE_GAP_MAX - PIPE_GAP_MIN + 1)) + PIPE_GAP_MIN;
    const minHeight = 50;
    const maxHeight = GAME_HEIGHT - pipeGap - minHeight - 20;
    const topHeight = Math.floor(Math.random() * (maxHeight - minHeight)) + minHeight;
    return {
      id: Math.random().toString(36).substr(2, 9),
      x: GAME_WIDTH + 10,
      topHeight,
      bottomY: topHeight + pipeGap,
      scored: false
    };
  }, []);

  // Handle game over
  const handleGameOver = useCallback((finalScore) => {
    const newHighScore = Math.max(highScore, finalScore);
    if (finalScore > highScore) {
      setHighScore(finalScore);
      // Save to server if it's a new record
      if (finalScore > serverHighScore) {
        saveHighScore(finalScore);
      }
    }
    setGameState("gameover");
  }, [highScore, serverHighScore, _vt, _vg]);

  // Reset and start game
  const startGame = useCallback(async () => {
    console.log("Starting game...");
    
    // Reset validation counters
    _xi.current = 0;
    _op.current = 0;
    
    // Initialize session with server
    if (user) {
      const session = await initGameSession();
      if (session) {
        set_vt(session.token);
        set_vg(session.startTime);
      }
    }
    
    setBatY(GAME_HEIGHT / 2 - BAT_HEIGHT / 2);
    setBatVelocity(JUMP_FORCE);
    setScore(0);
    setPipes([createPipe()]);
    setNewRecord(false);
    setGameState("playing");
  }, [createPipe, user]);

  // Handle tap/click
  const handleTap = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Tap detected, current state:", gameState);
    
    if (gameState === "idle" || gameState === "gameover") {
      startGame();
    } else if (gameState === "playing") {
      setBatVelocity(JUMP_FORCE);
      _xi.current += 1;  // Track interaction
    }
  }, [gameState, startGame]);

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === "Space" || e.key === " ") {
        e.preventDefault();
        if (gameState === "idle" || gameState === "gameover") {
          startGame();
        } else if (gameState === "playing") {
          setBatVelocity(JUMP_FORCE);
          _xi.current += 1;  // Track interaction
        }
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameState, startGame]);

  // Wing flapping animation
  useEffect(() => {
    if (gameState !== "playing") return;
    
    const interval = setInterval(() => {
      setWingUp(w => !w);
    }, 120);
    
    return () => clearInterval(interval);
  }, [gameState]);

  // Main game loop
  useEffect(() => {
    if (gameState !== "playing") return;

    let animationId;
    let lastTime = performance.now();

    const gameLoop = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 16.67; // Normalize to ~60fps
      lastTime = currentTime;

      setBatVelocity(v => {
        const newVelocity = v + GRAVITY * deltaTime;
        return newVelocity;
      });

      setBatY(currentY => {
        const newY = currentY + batVelocity * deltaTime;
        
        // Ground collision
        if (newY >= GAME_HEIGHT - BAT_HEIGHT - 5) {
          // Use setTimeout to avoid state update during render
          setTimeout(() => handleGameOver(score), 0);
          return GAME_HEIGHT - BAT_HEIGHT - 5;
        }
        
        // Ceiling
        if (newY < 5) {
          setBatVelocity(0);
          return 5;
        }
        
        return newY;
      });

      // Update pipes
      setPipes(currentPipes => {
        let updatedPipes = currentPipes
          .map(pipe => ({ ...pipe, x: pipe.x - PIPE_SPEED * deltaTime }))
          .filter(pipe => pipe.x > -PIPE_WIDTH - 10);

        // Add new pipe
        const lastPipe = updatedPipes[updatedPipes.length - 1];
        if (!lastPipe || lastPipe.x < GAME_WIDTH - 180) {
          updatedPipes = [...updatedPipes, createPipe()];
        }

        return updatedPipes;
      });

      animationId = requestAnimationFrame(gameLoop);
    };

    animationId = requestAnimationFrame(gameLoop);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [gameState, batVelocity, score, createPipe]);

  // Collision detection (separate effect for cleaner code)
  useEffect(() => {
    if (gameState !== "playing") return;

    const batLeft = BAT_X + BAT_HITBOX_PADDING;
    const batRight = BAT_X + BAT_WIDTH - BAT_HITBOX_PADDING;
    const batTop = batY + BAT_HITBOX_PADDING;
    const batBottom = batY + BAT_HEIGHT - BAT_HITBOX_PADDING;

    for (const pipe of pipes) {
      // Score when passing
      if (!pipe.scored && pipe.x + PIPE_WIDTH < BAT_X) {
        pipe.scored = true;
        setScore(s => s + 1);
        _op.current += 1;  // Track obstacle passed
      }

      // Collision check
      const pipeRight = pipe.x + PIPE_WIDTH;
      if (batRight > pipe.x && batLeft < pipeRight) {
        if (batTop < pipe.topHeight || batBottom > pipe.bottomY) {
          handleGameOver(score);
          break;
        }
      }
    }
  }, [batY, pipes, gameState, score, handleGameOver]);

  const batRotation = Math.min(40, Math.max(-20, batVelocity * 3));

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-muted-foreground mb-1">
          All Done for Today!
        </h3>
        <p className="text-sm text-muted-foreground">
          Play a quick game while you wait for tomorrow's challenges
        </p>
      </div>

      {/* Game Container */}
      <div
        ref={gameContainerRef}
        onClick={handleTap}
        onTouchEnd={handleTap}
        onMouseDown={(e) => e.preventDefault()}
        className="relative overflow-hidden rounded-2xl cursor-pointer select-none outline-none"
        style={{
          width: GAME_WIDTH,
          height: GAME_HEIGHT,
          backgroundColor: "#1f2937",
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
          userSelect: "none"
        }}
        tabIndex={0}
        data-testid="flappy-bat-game"
      >
        {/* Background */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, #374151 0%, #1f2937 100%)"
          }}
        />

        {/* Ceiling */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gray-900 pointer-events-none" />

        {/* Floor */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gray-900 pointer-events-none" />

        {/* Pipes */}
        {pipes.map((pipe) => (
          <div key={pipe.id} className="pointer-events-none">
            {/* Top Stalactite */}
            <div
              className="absolute"
              style={{
                left: pipe.x,
                top: 0,
                width: PIPE_WIDTH,
                height: pipe.topHeight,
                clipPath: "polygon(20% 0%, 80% 0%, 60% 100%, 40% 100%)",
                background: "linear-gradient(90deg, #4B5563 0%, #6B7280 50%, #4B5563 100%)"
              }}
            />
            
            {/* Bottom Stalagmite */}
            <div
              className="absolute"
              style={{
                left: pipe.x,
                top: pipe.bottomY,
                width: PIPE_WIDTH,
                height: GAME_HEIGHT - pipe.bottomY,
                clipPath: "polygon(40% 0%, 60% 0%, 80% 100%, 20% 100%)",
                background: "linear-gradient(90deg, #4B5563 0%, #6B7280 50%, #4B5563 100%)"
              }}
            />
          </div>
        ))}

        {/* Bat */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: BAT_X,
            top: batY,
            width: BAT_WIDTH,
            height: BAT_HEIGHT,
            transform: `rotate(${batRotation}deg)`,
            transformOrigin: "center",
            transition: "transform 0.05s linear"
          }}
        >
          <svg viewBox="0 0 60 40" width={BAT_WIDTH} height={BAT_HEIGHT}>
            {/* Left Wing */}
            <path
              d={wingUp 
                ? "M20,18 Q8,8 2,15 Q6,20 12,22 Q16,24 20,20 Z"
                : "M20,18 Q8,22 2,28 Q6,26 12,24 Q16,22 20,20 Z"
              }
              fill="#4B5563"
              stroke="#6B7280"
              strokeWidth="0.5"
            />
            <path
              d={wingUp ? "M20,18 L6,12" : "M20,18 L6,24"}
              stroke="#374151"
              strokeWidth="1"
              opacity="0.6"
            />
            
            {/* Right Wing */}
            <path
              d={wingUp
                ? "M40,18 Q52,8 58,15 Q54,20 48,22 Q44,24 40,20 Z"
                : "M40,18 Q52,22 58,28 Q54,26 48,24 Q44,22 40,20 Z"
              }
              fill="#4B5563"
              stroke="#6B7280"
              strokeWidth="0.5"
            />
            <path
              d={wingUp ? "M40,18 L54,12" : "M40,18 L54,24"}
              stroke="#374151"
              strokeWidth="1"
              opacity="0.6"
            />
            
            {/* Body */}
            <ellipse cx="30" cy="22" rx="12" ry="14" fill="#1f2937" />
            
            {/* Head */}
            <circle cx="30" cy="10" r="9" fill="#1f2937" />
            
            {/* Ears */}
            <polygon points="22,6 24,0 27,8" fill="#1f2937" />
            <polygon points="38,6 36,0 33,8" fill="#1f2937" />
            
            {/* Eyes */}
            <ellipse cx="25" cy="9" rx="4" ry="4" fill="#fbbf24" />
            <ellipse cx="35" cy="9" rx="4" ry="4" fill="#fbbf24" />
            <circle cx="26" cy="9" r="2" fill="#111827" />
            <circle cx="36" cy="9" r="2" fill="#111827" />
            
            {/* Eye shine */}
            <circle cx="24" cy="8" r="1" fill="white" opacity="0.7" />
            <circle cx="34" cy="8" r="1" fill="white" opacity="0.7" />
            
            {/* Nose */}
            <ellipse cx="30" cy="14" rx="2.5" ry="2" fill="#374151" />
            
            {/* Fangs */}
            <polygon points="28,17 29,21 30,17" fill="#f3f4f6" />
            <polygon points="32,17 31,21 30,17" fill="#f3f4f6" />
          </svg>
        </div>

        {/* Score */}
        <div 
          className="absolute top-4 left-1/2 -translate-x-1/2 text-4xl font-bold text-white pointer-events-none"
          style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.8)" }}
        >
          {score}
        </div>

        {/* Start Screen */}
        {gameState === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-10">
            <div className="text-center px-4">
              <div className="text-5xl mb-4">🦇</div>
              <h4 className="text-2xl font-bold text-white mb-2">Cave Bat</h4>
              <p className="text-gray-300 mb-6 text-sm">Navigate through the cave!</p>
              <motion.div 
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="inline-block px-8 py-3 bg-amber-500 rounded-xl text-white font-bold text-lg shadow-lg"
              >
                TAP TO START
              </motion.div>
            </div>
          </div>
        )}

        {/* Game Over Screen */}
        {gameState === "gameover" && (
          <div 
            className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 z-10"
          >
            <div className="text-center px-4">
              {newRecord && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="text-yellow-400 text-lg font-bold mb-2"
                >
                  🎉 NEW RECORD! 🎉
                </motion.div>
              )}
              <h4 className="text-2xl font-bold text-red-400 mb-4">Game Over!</h4>
              <div className="bg-gray-800 rounded-2xl p-6 mb-4 border border-gray-700">
                <p className="text-5xl font-bold text-amber-400">{score}</p>
                <p className="text-gray-400 text-sm mt-1">SCORE</p>
              </div>
              {highScore > 0 && (
                <p className="text-gray-300 mb-4">Best: {highScore}</p>
              )}
              <motion.div 
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="inline-block px-8 py-3 bg-amber-500 rounded-xl text-white font-bold text-lg shadow-lg cursor-pointer"
              >
                TAP TO RETRY
              </motion.div>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4 text-center max-w-xs">
        Tap the screen or press Spacebar to flap
        {user && <span className="block mt-1">Your scores are saved to the leaderboard!</span>}
      </p>
    </div>
  );
}
